import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";

// ── Social links config ────────────────────────────────────────────────────────

const SOCIAL_DEFS = [
  { key: "instagram", Icon: Instagram, label: "Instagram" },
  { key: "facebook",  Icon: Facebook,  label: "Facebook"  },
  { key: "youtube",   Icon: Youtube,   label: "YouTube"   },
  { key: "linkedin",  Icon: Linkedin,  label: "LinkedIn"  },
];

function socialHandle(key, url) {
  try {
    const parts = new URL(url).pathname.replace(/\/$/, "").split("/").filter(Boolean);
    if (key === "instagram" || key === "facebook") {
      return parts[0] ? `@${parts[0]}` : "Instagram";
    }
    if (key === "youtube") {
      const seg = parts[0] || "";
      if (seg.startsWith("@")) return seg;          // youtube.com/@handle
      if (parts.length >= 2) return `@${parts[1]}`; // /channel/id or /c/name
      return "YouTube";
    }
    if (key === "linkedin") {
      return parts[1] || parts[0] || "LinkedIn";    // /company/name
    }
  } catch { /* fall through */ }
  return SOCIAL_DEFS.find((d) => d.key === key)?.label ?? key;
}

// ── Footer ─────────────────────────────────────────────────────────────────────

const Footer = () => {
  const info = useCompanyInfo();

  const logoSrc = info.logo_url?.trim() || "/images/1-logo/yatay/yatay2.png";

  return (
    <footer className="bg-[#111111] py-16 md:py-32" data-testid="footer">
      <div className="footer-shell max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="footer-grid grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-16">

          {/* ── Brand ─────────────────────────────────────────────── */}
          <div className="footer-brand col-span-2 md:col-span-1">
            <div className="mb-4">
              <img
                src={logoSrc}
                alt="Ober Yapı — Konya İnşaat ve Villa Projeleri"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </div>
            <p className="text-[#86868B] text-sm leading-relaxed">
              Yaşanacak her yer, bir eser.
            </p>
          </div>

          {/* ── Hızlı Bağlantılar ─────────────────────────────────── */}
          <div className="footer-links-group">
            <h3 className="text-[#F5F5F7] font-medium text-base md:text-lg mb-4 md:mb-6">
              Hızlı Bağlantılar
            </h3>
            <ul className="space-y-3">
              {[
                { to: "/",           label: "Ana Sayfa",  testId: "footer-link-ana-sayfa"  },
                { to: "/hakkimizda", label: "Hakkımızda", testId: "footer-link-hakkimizda" },
                { to: "/hizmetler",  label: "Hizmetler",  testId: "footer-link-hizmetler"  },
                { to: "/projeler",   label: "Projeler",   testId: "footer-link-projeler"   },
                { to: "/iletisim",   label: "İletişim",   testId: "footer-link-iletisim"   },
              ].map(({ to, label, testId }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors"
                    data-testid={testId}
                  >
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/dreams"
                  className="font-signature text-lg signature-gold"
                  data-testid="footer-link-dreams"
                >
                  Dreams
                </Link>
              </li>
            </ul>
          </div>

          {/* ── Hizmetler ─────────────────────────────────────────── */}
          <div className="footer-services-group">
            <h3 className="text-[#F5F5F7] font-medium text-base md:text-lg mb-4 md:mb-6">
              Hizmetlerimiz
            </h3>
            <ul className="space-y-3">
              {[
                "Konut Projeleri",
                "Ticari Yapılar",
                "İnşaat Mühendisliği",
                "Proje Yönetimi",
                "Danışmanlık",
              ].map((s) => (
                <li key={s} className="text-[#86868B] text-sm">{s}</li>
              ))}
            </ul>
          </div>

          {/* ── İletişim ──────────────────────────────────────────── */}
          <div className="footer-contact col-span-2 md:col-span-1">
            <h3 className="text-[#F5F5F7] font-medium text-base md:text-lg mb-4 md:mb-6">
              İletişim
            </h3>
            <ul className="space-y-4">
              {info.address?.trim() && (
                <li className="flex items-start gap-3 text-[#86868B] text-sm">
                  <MapPin size={18} className="text-[#C5A059] mt-1 flex-shrink-0" />
                  <span>{info.address}</span>
                </li>
              )}

              {info.phone?.trim() && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Phone size={18} className="text-[#C5A059] flex-shrink-0" />
                  <a
                    href={`tel:${info.phone.replace(/\s/g, "")}`}
                    className="hover:text-[#C5A059] transition-colors"
                  >
                    {info.phone}
                  </a>
                </li>
              )}

              {info.email?.trim() && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Mail size={18} className="text-[#C5A059] flex-shrink-0" />
                  <a
                    href={`mailto:${info.email}`}
                    className="hover:text-[#C5A059] transition-colors break-all"
                  >
                    {info.email}
                  </a>
                </li>
              )}

              {info.working_hours?.trim() && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Clock size={18} className="text-[#C5A059] flex-shrink-0" />
                  <span>{info.working_hours}</span>
                </li>
              )}

              {SOCIAL_DEFS.filter(({ key }) => info[key]?.trim()).map(({ key, Icon }) => (
                <li key={key} className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Icon size={18} className="text-[#C5A059] flex-shrink-0" strokeWidth={1.5} />
                  <a
                    href={info[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#C5A059] transition-colors"
                  >
                    {socialHandle(key, info[key])}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Bottom bar ────────────────────────────────────────────── */}
        <div className="footer-bottom border-t border-white/10 pt-8">
          <div className="footer-bottom-row flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#555555] text-sm">
              © {new Date().getFullYear()} Ober Yapı. Tüm hakları saklıdır.
            </p>
            <div className="flex gap-6">
              <Link
                to="/gizlilik-politikasi"
                className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors"
              >
                Gizlilik Politikası
              </Link>
              <Link
                to="/kullanim-sartlari"
                className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors"
              >
                Kullanım Şartları
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
